<!--

Avatar component shows user avatar or user initials if avatar is not set.
Additionally it can show other data like name, phone, custom subtext, availability toggle switch and availability status icon.

@param gradient1 Top gradient color for the avatar background
@param gradient2 Bottom gradient color for the avatar background
@param showAvailabilityToggle boolean Flag which shows/hides the user status and status change option in the dropdown. (works only for agents)
@param size String Defines the css class for user button size. Use enumerated values from UserDropdownButtonSize.
@param showAvailabilityStatus If set it will show green or red dot on top of the user avatar. (works only for agents)
@param showName Shows the neme next to the avatar.
@param subText Shows text below the name. It will override availability toggle and phone if they are also set to be shown.
@param showPhone Shows phone number below the name. It will override availability toggle if it is also set to be shown.
@param person Person to be rendered by avatar component which overrides default render of currently logged in user.
@param showClient Set the component to show phone number since client can't have availability.
@param showLink Shows the link to agent/client page.
@param characterCount Defines how many characters should be on the avatar.
@param hideAvatarImage Hides the avatarImage since it will show the avatar image from loged in user. TODO This needs to be refactored and fixed when The whole component gets refactored.

TODO - we have VUEX binding on userName and also can provide person that has legal_name and name. We should decide to stick with one value that will be used everywhere.
TODO - show client logic is a bit off - refactor it

-->

<template>
  <div
    class="sb-avatar flex flex-align-center"
    :style="cssVars"
    @click="click" >
    <span
      v-if="!showClient"
      class="logo flex center-all"
      :class="[size]"
      :style="!hideAvatarImage && avatar ? `background-image: url(${avatar})` : ''" >
      <span v-if="!hideAvatarImage && avatar" />
      <span
        v-else
        class="avatar-placeholder" />
      <span
        v-if="showAvailabilityStatus"
        :class="[userAvailable ? 'green' : 'red']"
        class="indicator"
      />
    </span>
    <span
      v-else
      class="logo flex center-all client"
      :class="[size]"
      :style="!hideAvatarImage && person && person.avatar ? `background-image: url(${person.avatar})` : ''" >
      <span v-if="!hideAvatarImage && person && person.avatar" />
      <template v-else-if="person && person.name && !person.avatar">{{ person.name | initials(characterCount) }}</template>
      <span
        v-else
        class="avatar-placeholder" />
      <span
        v-if="showAvailabilityStatus"
        :class="[userAvailable ? 'green' : 'red']"
        class="indicator" />
    </span>
    <span
      v-if="showName"
      class="data greedy ellipsis">
      <div class="flex justify-start flex-align-center hover-wrapper">
        <p class="title ellipsis">{{ showClient && person ? (person.name ? person.name : person.phone) : userName | phoneFormat }}</p>
        <CopyNumber
          v-if="!person || !person.name"
          :value="person ? person.phone : ''"
          class="big-copy show-on-hover no-shrink"
          @click.native.stop />
      </div>
      <div
        v-if="showAvailabilityToggle && (!SubText || !showPhone)"
        class="availability">
        <SwitchCheckbox
          id="userAvailabilitySwitch"
          :value="userAvailable"
          class="dropdown-check"
          size="micro"
          outline-color="#B2ECE4"
          switch-color="#00bfa5"
          off-outline-color="#ffcdcf"
          off-switch-color="#ff5b61"
          @input="changeUserAvailability"
        />
        <span :class="userAvailable ? '' : 'unavailable'">{{ userAvailable ? 'Available' : 'Away' }}</span>
      </div>
      <div
        v-if="showPhone && person && person.name"
        class="flex justify-start flex-align-center hover-wrapper">
        <p
          class="subtext ellipsis">{{ person.phone | phoneFormat }}</p>
        <CopyNumber
          class="show-on-hover no-shrink"
          :value="person.phone"
          @click.native.stop />
      </div>
    </span>
    <div
      v-if="showLink"
      class="profile-link">
      <router-link
        :to="person ? {
          name: 'ContactProfile',
          params: {
            contact_id: person.id,
          }
        } : {}"
        target="_blank"
      >
        <icon
          name="external-link-alt"
          height="14px" />
      </router-link>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.show-on-hover {
  display: none;
}

.hover-wrapper:hover {
  .show-on-hover {
    display: block;
  }
}

.sb-avatar {
  padding: 16px 12px 12px;
  padding: 0;
  cursor: default;
  min-width: 1px; // CS this is helpfull when there is a one more element after the avatar and they are wrapped in a flex row. In this case the name can have the dynamic width and ellipsis is shown. Check UserNotes component for example.

  .logo {
    flex-shrink: 0;
    position: relative;
    display: block;
    background: linear-gradient(var(--gradient1), var(--gradient2));
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    color: var(--text-color);
    border-radius: 50%;
    font-size: 12px;
    text-transform: uppercase;
    text-align: center;

    .avatar-placeholder {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;

      mask-image: url(~@/assets/img/SB-Icons/profile.svg);
      mask-size: 65% 65%;
      mask-repeat: no-repeat;
      mask-position: center center;
      background-color: $white;
    }

    // &.client {}

    &.xs-button {
      width: 20px !important;
      height: 20px !important;
      font-size: 8px !important;
      line-height: 20px !important;
      .indicator {
        width: 11px;
        height: 11px;
      }
      .avatar-placeholder {
        mask-size: 62% 62%;
      }
    }
    &.s-button {
      width: 24px !important;
      height: 24px !important;
      font-size: 12px !important;
      font-weight: 500 !important;
      line-height: 24px !important;
      &-26 {
        width: 26px !important;
        height: 26px !important;
        font-size: 12px !important;
        line-height: 26px !important;
      }
    }
    &.m-button {
      width: 30px !important;
      height: 30px !important;
      font-size: 15px !important;
      line-height: 30px !important;
      font-weight: 500 !important;
    }
    &.l-button {
      width: 34px !important;
      height: 34px !important;
      font-size: 17px !important;
      font-weight: 500 !important;
      line-height: 17px !important;
    }
    &.xl-button {
      width: 40px !important;
      height: 40px !important;
      font-size: 20px !important;
      line-height: 40px !important;
    }
    &.xxl-button {
      width: 56px !important;
      height: 56px !important;
      font-size: 26px !important;
      line-height: 56px !important;
    }

    &.conversation-avatar {
      width: 14px !important;
      height: 14px !important;
      font-size: 10px !important;
      line-height: 14px !important;
      // margin-right: 5px;
    }

    @import "../../assets/css/modules/partials/indicator";
  }

  .data {
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    margin: 0 0 0 8px;

    .title {
      font-size: 14px;
      font-weight: 500;
      color: $blackPrimary;
      margin: 0;
      min-height: auto !important;
      line-height: initial !important;
    }

    .availability {
      display: flex;

      .dropdown-check {
        display: inline-block;
      }

      span {
        font-size: 12px;
        font-weight: 500;
        line-height: 11px;
        margin-left: 4px;
        color: $greenPrimary;

        &.unavailable {
          color: $redPrimary;
        }
      }
    }

    .subtext {
      font-size: 12px !important;
      margin: 0;
      padding: 0;
      color: $osloGray;
      font-weight: 400;
    }
  }

  .profile-link {
    a {
      color: $cadetBlue;
      &:hover {
        color: $blackPrimary;
      }
    }
  }
}
</style>

<script>
import UserAvailabilityMixin from '@/mixins/UserAvailability';
import SwitchCheckbox from './SwitchCheckbox';
import UserDropdownButtonSize from '../../helpers/lists/userDropdownButtonSize';
import SBColors from '@/helpers/lists/colors';
import CopyNumber from './CopyNumber';

export default {
  name: 'SBAvatar',
  components: {
    SwitchCheckbox,
    CopyNumber,
  },
  mixins: [UserAvailabilityMixin],
  props: {
    gradient1: {
      type: String,
      required: false,
      default: SBColors.avatar1,
    },
    gradient2: {
      type: String,
      required: false,
      default: SBColors.avatar2,
    },
    textColor: {
      type: String,
      required: false,
      default: SBColors.white,
    },
    showAvailabilityStatus: {
      type: Boolean,
      required: false,
      default: false,
    },
    showAvailabilityToggle: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: String,
      required: false,
      default: UserDropdownButtonSize.M,
    },
    showName: {
      type: Boolean,
      required: false,
      default: false,
    },
    subText: {
      // TODO this is not implemented yet, do it in the next iteration
      type: String,
      required: false,
      default: '',
    },
    showPhone: {
      type: Boolean,
      required: false,
      default: false,
    },
    person: {
      type: Object,
      required: false,
      default: () => {},
    },
    showClient: {
      type: Boolean,
      required: false,
      default: false,
    },
    showLink: {
      type: Boolean,
      required: false,
      default: false,
    },
    characterCount: {
      type: Number,
      required: false,
      default: 2,
    },
    hideAvatarImage: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      SBColors,
    };
  },
  computed: {
    cssVars() {
      return {
        '--gradient1': this.gradient1,
        '--gradient2': this.gradient2,
        '--text-color': this.textColor,
      };
    },
  },
  mounted() {},
  methods: {
    click() {
      this.$emit('action');
    },
  },
};
</script>
